import { compareNullableValues } from '@shigo-ui/utils';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  useDashboardDataTableState,
  type DashboardDataTableColumn,
  type DashboardDataTableSortState,
} from '@shigo-ui/dashboard-ui';

import { dashboardT } from '../../../../../lib/messages';

type networkDashboardTableProps<TItem> = {
  title: string;
  items: TItem[];
  columns: DashboardDataTableColumn<TItem>[];
  getRowKey: (item: TItem) => string;
  sortValue: (item: TItem, key: string) => string | number | undefined;
  pageSize: number;
  paginated?: boolean | undefined;
  loading: boolean;
  errorMessage?: string | undefined;
  minWidthClassName: string;
};

export function NetworkDashboardTable<TItem>({
  title,
  items,
  columns,
  getRowKey,
  sortValue,
  pageSize,
  paginated = true,
  loading,
  errorMessage,
  minWidthClassName,
}: networkDashboardTableProps<TItem>) {
  const table = useDashboardDataTableState({
    initialPageSize: pageSize,
    totalItems: items.length,
    pageSizes: [5, 10, 25, 50, 100],
    disabled: loading,
  });
  const { page, pageSize: size, sort } = table;
  const sorted = [...items].sort((left, right) => compare(left, right, sort, sortValue));
  const visible = paginated
    ? sorted.slice(page * size, page * size + size)
    : sorted.slice(0, pageSize);
  return (
    <DashboardDataTable
      className="max-w-none"
      tableLabel={title}
      columns={columns}
      items={visible}
      getRowKey={getRowKey}
      loading={loading}
      loadingMessage={dashboardT('dashboard.widget.loading')}
      errorMessage={errorMessage}
      emptyMessage={dashboardT('dashboard.widget.empty')}
      minWidthClassName={minWidthClassName}
      surfaceClassName="rounded-none border-0 shadow-none"
      sort={table.sortProps}
      pagination={paginated ? table.pagination : undefined}
    />
  );
}

function compare<TItem>(
  left: TItem,
  right: TItem,
  sort: DashboardDataTableSortState | undefined,
  sortValue: networkDashboardTableProps<TItem>['sortValue'],
) {
  if (!sort) return 0;
  return compareNullableValues(
    sortValue(left, sort.key),
    sortValue(right, sort.key),
    sort.direction === DashboardDataTableSortDirection.Desc,
  );
}
